import { memo } from "react";
import { useSelector } from "react-redux";

import Avatar from "./Avatar";
import {
  selectUserDirectoryEntry,
  type UserDirectoryRootState,
} from "../../app/userDirectory/userDirectorySlice";

interface UserAvatarProps {
  /** ユーザー ID。Redux userDirectory から display_name / avatar を解決するためのキー。 */
  userId?: number | string | null;
  /** 呼び出し側が既に保持しているフォールバックの avatar URL（優先度: props > directory）。 */
  avatar?: string | null;
  /** 呼び出し側が保持している表示名のフォールバック（優先度: props > directory）。 */
  displayName?: string | null;
  /** アバターサイズ（px）。Avatar コンポーネントに委譲。 */
  size?: number;
  className?: string;
  /** ツールチップ。未指定なら display_name を使う。 */
  tooltipText?: string;
  tooltipPlacement?: React.ComponentProps<typeof Avatar>["tooltipPlacement"];
  tooltipOffset?: [number, number];
  isOnline?: boolean;
  /** 選択可能なリスト要素の一つとして振る舞う場合 true（ホバー時に青 outline）。 */
  selectable?: boolean;
  /** 選択中フラグ（常時、青 outline と薄青背景）。 */
  selected?: boolean;
  /** クリック時のハンドラ（`selectable` が true のときのみ有効）。 */
  onClick?: () => void;
}

/**
 * userId に紐づくユーザー情報を Redux userDirectory から解決し、Avatar を描画する。
 *
 * - props で明示的に渡された `avatar` / `displayName` を最優先で使う。
 * - 欠けている情報は Redux の userDirectory から解決する（WebSocket 経由で差し込まれた
 *   プレースホルダ receiver で avatar が null の場合にこの経路で救済される）。
 * - どうしても解決できない場合は Avatar コンポーネントのフォールバック
 *   （頭文字/人型アイコン）が表示される。
 */
const UserAvatar: React.FC<UserAvatarProps> = memo(
  ({
    userId,
    avatar,
    displayName,
    size,
    className,
    tooltipText,
    tooltipPlacement,
    tooltipOffset,
    isOnline,
    selectable,
    selected,
    onClick,
  }) => {
    // Redux subscribe。userId が無ければ undefined を返すセレクタで no-op 相当。
    const entry = useSelector((state: UserDirectoryRootState) =>
      selectUserDirectoryEntry(userId)(state),
    );

    // props を最優先、足りない部分を directory が補完。
    const resolvedAvatar = avatar || entry?.avatar || undefined;
    const resolvedName = displayName || entry?.display_name || undefined;

    return (
      <Avatar
        size={size}
        src={resolvedAvatar || undefined}
        name={resolvedName}
        className={className}
        tooltipText={tooltipText}
        tooltipPlacement={tooltipPlacement}
        tooltipOffset={tooltipOffset}
        isOnline={isOnline}
        selectable={selectable}
        selected={selected}
        onClick={onClick}
      />
    );
  },
);

UserAvatar.displayName = "UserAvatar";

export default UserAvatar;
